/**
 * Day/Night mode helpers. Computes the active mode based on the profile's
 * day_start/night_start times and override setting.
 *
 * - "auto" : compare current time vs day/night start times
 * - "day"  : force day, ignore time
 * - "night": force night, ignore time
 */

function parseHHMM(s) {
  if (!s || typeof s !== "string") return null;
  const m = s.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

export function computeMode(profile, now = new Date()) {
  if (!profile) return "day";
  const override = profile.mode_override || "auto";
  if (override === "day" || override === "night") return override;
  const dayStart = parseHHMM(profile.day_start) ?? 7 * 60;
  const nightStart = parseHHMM(profile.night_start) ?? 20 * 60;
  const cur = now.getHours() * 60 + now.getMinutes();
  // Day window: dayStart <= cur < nightStart (assuming day starts before night
  // on the same calendar day — which is always true for human schedules).
  if (dayStart <= nightStart) {
    return cur >= dayStart && cur < nightStart ? "day" : "night";
  }
  // Edge case: if night_start < day_start (e.g. day from 22:00 to 06:00)
  return cur >= dayStart || cur < nightStart ? "day" : "night";
}

/**
 * Returns the next time we should re-check the mode, in milliseconds. We
 * round to the next 15-minute boundary so all profiles tick in lockstep
 * and we don't accidentally hammer the CPU.
 */
export function msUntilNextCheck(now = new Date()) {
  const t = new Date(now);
  t.setSeconds(0, 0);
  t.setMinutes(Math.floor(t.getMinutes() / 15) * 15 + 15);
  return Math.max(15_000, t.getTime() - now.getTime());
}
